import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PublicShell } from "@/components/PublicShell";
import { toast } from "sonner";
import { FloppyDisk, Paperclip, ShieldCheck } from "@phosphor-icons/react";

const WORK_AUTH = [["authorized", "Yes, I am legally authorised to work in the United States"],
                   ["authorized_with_sponsorship", "I would need sponsorship to work in the United States"],
                   ["prefer_not_to_answer", "I prefer to discuss this with you directly"]];
const DEMOGRAPHIC_QUESTIONS = [
  ["gender", "Gender", ["Woman", "Man", "Another gender identity", "I prefer not to answer"]],
  ["race_ethnicity", "Race or ethnicity", ["American Indian or Alaska Native", "Asian",
    "Black or African American", "Hispanic or Latino", "Native Hawaiian or Pacific Islander", "White",
    "Two or more races", "I prefer not to answer"]],
  ["veteran_status", "Veteran status", ["I am a protected veteran", "I am not a protected veteran",
    "I prefer not to answer"]],
];

const Section = ({ title, hint, children, testid }) => (
  <section className="bg-white rounded-2xl border border-border p-6 sm:p-7" data-testid={testid}>
    <h2 className="text-base md:text-lg font-bold">{title}</h2>
    {hint && <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{hint}</p>}
    <div className="mt-5 space-y-4">{children}</div>
  </section>
);

const Field = ({ label, children }) => (
  <div><Label className="font-bold">{label}</Label><div className="mt-2">{children}</div></div>
);

const RowEditor = ({ rows, columns, onChange, addLabel, testid, max = 6 }) => (
  <div className="space-y-3" data-testid={testid}>
    {rows.map((row, i) => (
      <div key={row._k} className="rounded-xl border border-border p-4 grid gap-3 sm:grid-cols-2">
        {columns.map(([key, label]) => (
          <div key={key} className={key === "duties" ? "sm:col-span-2" : ""}>
            <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</Label>
            <Input className="mt-1.5" value={row[key] || ""} data-testid={`${testid}-${i}-${key}`}
                   onChange={(e) => onChange(rows.map((r, j) => (j === i ? { ...r, [key]: e.target.value } : r)))} />
          </div>
        ))}
        <Button type="button" variant="link" className="px-0 text-[#EF4444] justify-self-start sm:col-span-2"
                data-testid={`${testid}-remove-${i}`}
                onClick={() => onChange(rows.filter((_, j) => j !== i))}>Remove</Button>
      </div>
    ))}
    {rows.length < max && (
      <Button type="button" variant="outline" className="rounded-full font-bold" data-testid={`${testid}-add`}
              onClick={() => onChange([...rows, { _k: `${testid}-${Date.now()}` }])}>{addLabel}</Button>
    )}
  </div>
);

const keyed = (rows) => (rows || []).map((r, i) => ({ ...r, _k: r._k || `row-${i}-${Math.random().toString(36).slice(2, 7)}` }));
const stripped = (rows) => rows.map(({ _k, ...rest }) => rest);

function Disclosure({ token, onDone }) {
  const [doc, setDoc] = useState(null);
  const [form, setForm] = useState({ typed_name: "", agree: false, dob: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get(`/careers/applications/${token}/disclosure`).then(({ data }) => setDoc(data)); }, [token]);
  if (!doc?.pending) return null;

  const sign = async () => {
    setBusy(true);
    try {
      await api.post(`/careers/applications/${token}/disclosure/sign`, form);
      toast.success("Authorization signed");
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Section title="Background check authorization" testid="disclosure-panel"
             hint="This is a separate document from your application. Please read it before signing.">
      <pre className="whitespace-pre-wrap text-sm leading-relaxed bg-slate-50 rounded-xl p-5 max-h-72 overflow-y-auto"
           data-testid="disclosure-text">{doc.text}</pre>
      <Field label="Date of birth">
        <Input type="date" value={form.dob} data-testid="disclosure-dob"
               onChange={(e) => setForm({ ...form, dob: e.target.value })} />
        <p className="mt-2 text-xs text-muted-foreground">{doc.dob_reason}</p>
      </Field>
      <Field label="Type your full legal name to sign">
        <Input value={form.typed_name} data-testid="disclosure-name"
               onChange={(e) => setForm({ ...form, typed_name: e.target.value })} />
      </Field>
      <label className="flex gap-3 items-start text-sm font-semibold">
        <Checkbox checked={form.agree} data-testid="disclosure-agree"
                  onCheckedChange={(v) => setForm({ ...form, agree: Boolean(v) })} />
        I have read the disclosure above and I authorise these checks.
      </label>
      <Button onClick={sign} disabled={busy || !form.agree} className="rounded-full font-bold bg-[#3B82F6]"
              data-testid="disclosure-sign-btn">
        <ShieldCheck className="mr-2" /> {busy ? "Signing…" : "Sign authorization"}
      </Button>
    </Section>
  );
}

function SubmittedView({ record, token, reload }) {
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/careers/applications/${token}/respond`, { body: reply });
      setReply(""); toast.success("Sent to our hiring team"); reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file); fd.append("kind", "requested");
    try { await api.post(`/careers/applications/${token}/files`, fd); toast.success("Document received"); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const consent = async (opt_in) => {
    try { await api.post(`/careers/applications/${token}/pool-consent`, { opt_in }); toast.success("Saved"); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const withdraw = async () => {
    if (!window.confirm("Withdraw this application? We will keep the record but stop considering you for this role.")) return;
    try { await api.post(`/careers/applications/${token}/withdraw`); toast.success("Withdrawn"); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mt-8 space-y-5">
      <Section title="Your application status" testid="application-status">
        <p className="text-2xl font-bold" data-testid="stage-label">{record.stage_label}</p>
        <p className="text-sm text-muted-foreground">
          {record.position_title} · submitted {record.submitted_at ? new Date(record.submitted_at).toLocaleDateString() : ""}
        </p>
      </Section>

      <Disclosure token={token} onDone={reload} />

      <Section title="Messages with our hiring team" testid="applicant-messages"
               hint="Anything you send here goes only to the people reviewing your application.">
        {record.requests.length === 0 && <p className="text-sm text-muted-foreground">No messages yet.</p>}
        {record.requests.map((m, i) => (
          <div key={`${m.at}-${i}`} className={`rounded-xl p-4 text-sm ${m.direction === "to_applicant" ? "bg-blue-50" : "bg-slate-50"}`}>
            <p className="font-bold">{m.direction === "to_applicant" ? "Teach Play to Learn" : "You"}
              <span className="ml-2 text-xs font-semibold text-muted-foreground">{new Date(m.at).toLocaleString()}</span></p>
            <p className="mt-1.5 whitespace-pre-line">{m.body}</p>
          </div>
        ))}
        <Textarea value={reply} placeholder="Reply or send the information we asked for" className="min-h-24"
                  data-testid="applicant-reply" onChange={(e) => setReply(e.target.value)} />
        <div className="flex flex-wrap gap-3">
          <Button onClick={send} disabled={busy || !reply.trim()} className="rounded-full font-bold bg-[#3B82F6]"
                  data-testid="send-reply-btn">Send</Button>
          <label className="inline-flex items-center gap-2 text-sm font-bold text-[#3B82F6] cursor-pointer">
            <Paperclip size={16} /> Attach a document
            <input type="file" className="hidden" data-testid="requested-upload"
                   onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
          </label>
        </div>
      </Section>

      <Section title="Future openings" testid="pool-consent-panel">
        <p className="text-sm text-muted-foreground">
          {record.pool_consent?.opt_in
            ? "You are in our future-opportunities pool. We may contact you about roles that match your background."
            : "You are not in our future-opportunities pool."}
        </p>
        <Button variant="outline" className="rounded-full font-bold" data-testid="toggle-pool-btn"
                onClick={() => consent(!record.pool_consent?.opt_in)}>
          {record.pool_consent?.opt_in ? "Stop contacting me about future openings" : "Contact me about future openings"}
        </Button>
      </Section>

      {record.stage !== "withdrawn" && record.stage !== "hired" && (
        <Button variant="outline" className="rounded-full font-bold text-[#EF4444]" onClick={withdraw}
                data-testid="withdraw-btn">Withdraw my application</Button>
      )}
    </div>
  );
}

export default function ApplicationWorkspace() {
  const { token } = useParams();
  const [record, setRecord] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState(null);
  const [ack, setAck] = useState({ acknowledged: false, typed_name: "", pool_opt_in: false, demographics: {} });
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/careers/applications/${token}`);
      setRecord(data);
      if (!data.submitted) {
        const a = data.answers_snapshot || {};
        setForm({
          preferred_name: a.preferred_name || "", phone: a.phone || "", city: a.city || "", state: a.state || "",
          availability: a.availability || "", preferred_schedule: a.preferred_schedule || "",
          earliest_start: a.earliest_start || "", childcare_years: a.childcare_years || "",
          work_authorization: a.work_authorization || "", cover_note: a.cover_note || "",
          references_may_contact: a.references_may_contact, answers: a.answers || {},
          experience: keyed(a.experience), education: keyed(a.education),
          certifications: keyed(a.certifications), references: keyed(a.references),
          accommodation_request: "",
        });
      }
    } catch (e) { setError(apiError(e.response?.data?.detail)); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async (quiet = false) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/careers/applications/${token}`, {
        ...form, experience: stripped(form.experience), education: stripped(form.education),
        certifications: stripped(form.certifications), references: stripped(form.references) });
      setSavedAt(data.saved_at);
      if (!quiet) toast.success("Draft saved — your link will bring you back here");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await save(true);
      await api.post(`/careers/applications/${token}/submit`, ack);
      toast.success("Application submitted. Thank you!");
      setForm(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const upload = async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file); fd.append("kind", kind);
    try { await api.post(`/careers/applications/${token}/files`, fd); toast.success("Uploaded"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (error) return <PublicShell testid="application-workspace"><p className="font-semibold text-[#EF4444]" data-testid="workspace-error">{error}</p></PublicShell>;
  if (!record) return <PublicShell testid="application-workspace"><p className="text-muted-foreground">Loading your application…</p></PublicShell>;

  const questions = record.position?.questions || [];
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <PublicShell testid="application-workspace">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#3B82F6]">
        {record.is_pool ? "Future opportunities" : "Employment application"}
      </p>
      <h1 className="mt-2 text-4xl sm:text-5xl font-bold tracking-tight">{record.position_title}</h1>

      {record.submitted ? (
        <SubmittedView record={record} token={token} reload={load} />
      ) : !form ? (
        <p className="mt-8 text-muted-foreground">Loading your answers…</p>
      ) : (
        <div className="mt-8 space-y-5">
          <p className="text-sm text-muted-foreground max-w-2xl">
            Your answers save when you select Save draft. Nothing is sent to us until you submit at the bottom.
            {record.link_expires_at ? ` Your private link works until ${new Date(record.link_expires_at).toLocaleDateString()}.` : ""}
          </p>

          <Section title="Your résumé" testid="resume-section"
                   hint="Upload a PDF or Word résumé, or skip it and fill the application in by hand. Either way, please complete the questions below.">
            <div className="flex flex-wrap gap-4 items-center">
              <label className="inline-flex items-center gap-2 text-sm font-bold text-[#3B82F6] cursor-pointer">
                <Paperclip size={16} /> Upload résumé
                <input type="file" className="hidden" accept=".pdf,.doc,.docx" data-testid="resume-upload"
                       onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], "resume")} />
              </label>
              <label className="inline-flex items-center gap-2 text-sm font-bold text-[#3B82F6] cursor-pointer">
                <Paperclip size={16} /> Cover letter (optional)
                <input type="file" className="hidden" accept=".pdf,.doc,.docx" data-testid="cover-upload"
                       onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], "cover_letter")} />
              </label>
              <label className="inline-flex items-center gap-2 text-sm font-bold text-[#3B82F6] cursor-pointer">
                <Paperclip size={16} /> Credential or certificate (optional)
                <input type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" data-testid="credential-upload"
                       onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], "credential")} />
              </label>
            </div>
            {record.files.length > 0 && (
              <ul className="text-sm space-y-1" data-testid="uploaded-files">
                {record.files.map((f) => <li key={f.id} className="font-semibold">{f.kind.replace("_", " ")}: {f.filename}</li>)}
              </ul>
            )}
          </Section>

          <Section title="About you" testid="about-section">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Preferred name (optional)"><Input value={form.preferred_name} data-testid="preferred-name" onChange={set("preferred_name")} /></Field>
              <Field label="Phone"><Input value={form.phone} data-testid="app-phone" onChange={set("phone")} /></Field>
              <Field label="City"><Input value={form.city} data-testid="app-city" onChange={set("city")} /></Field>
              <Field label="State"><Input value={form.state} data-testid="app-state" onChange={set("state")} /></Field>
            </div>
          </Section>

          <Section title="Availability" testid="availability-section">
            <Field label="Your availability">
              <Textarea value={form.availability} className="min-h-24" data-testid="app-availability"
                        placeholder="For example: Monday to Friday, 7:00 AM to 4:00 PM" onChange={set("availability")} />
            </Field>
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Preferred schedule"><Input value={form.preferred_schedule} data-testid="app-schedule" onChange={set("preferred_schedule")} /></Field>
              <Field label="Earliest start date"><Input type="date" value={form.earliest_start} data-testid="app-start" onChange={set("earliest_start")} /></Field>
              <Field label="Years in child care"><Input value={form.childcare_years} data-testid="app-years" onChange={set("childcare_years")} /></Field>
            </div>
          </Section>

          <Section title="Work experience" testid="experience-section">
            <RowEditor rows={form.experience} testid="experience" addLabel="Add a role"
                       columns={[["employer", "Employer"], ["title", "Job title"], ["start", "From"], ["end", "To"], ["duties", "What you did"]]}
                       onChange={(rows) => setForm({ ...form, experience: rows })} />
          </Section>

          <Section title="Education and training" testid="education-section">
            <RowEditor rows={form.education} testid="education" addLabel="Add education"
                       columns={[["school", "School or programme"], ["credential", "Credential"], ["year", "Year"]]}
                       onChange={(rows) => setForm({ ...form, education: rows })} />
          </Section>

          <Section title="Certifications" testid="certifications-section"
                   hint="For example CPR, First Aid, a CDA, or a state child care credential.">
            <RowEditor rows={form.certifications} testid="certifications" addLabel="Add a certification"
                       columns={[["name", "Certification"], ["issuer", "Issued by"], ["expires", "Expires"]]}
                       onChange={(rows) => setForm({ ...form, certifications: rows })} />
          </Section>

          <Section title="References" testid="references-section">
            <RowEditor rows={form.references} testid="references" addLabel="Add a reference" max={4}
                       columns={[["name", "Name"], ["relationship", "Relationship"], ["phone", "Phone"], ["email", "Email"]]}
                       onChange={(rows) => setForm({ ...form, references: rows })} />
            <Field label="May we contact your references?">
              <Select value={form.references_may_contact === null || form.references_may_contact === undefined ? "" : String(form.references_may_contact)}
                      onValueChange={(v) => setForm({ ...form, references_may_contact: v === "true" })}>
                <SelectTrigger data-testid="references-consent"><SelectValue placeholder="Choose an answer" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Yes, you may contact them</SelectItem>
                  <SelectItem value="false">Please ask me first</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </Section>

          <Section title="Work authorisation" testid="work-auth-section">
            <Select value={form.work_authorization} onValueChange={(v) => setForm({ ...form, work_authorization: v })}>
              <SelectTrigger data-testid="work-auth"><SelectValue placeholder="Choose an answer" /></SelectTrigger>
              <SelectContent>{WORK_AUTH.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </Section>

          {questions.length > 0 && (
            <Section title="Questions for this position" testid="position-questions">
              {questions.map((q) => (
                <Field key={q.key} label={`${q.label}${q.required ? " *" : ""}`}>
                  {q.type === "long_text" ? (
                    <Textarea className="min-h-24" value={form.answers[q.key] || ""} data-testid={`question-${q.key}`}
                              onChange={(e) => setForm({ ...form, answers: { ...form.answers, [q.key]: e.target.value } })} />
                  ) : q.type === "yes_no" || q.type === "choice" ? (
                    <Select value={form.answers[q.key] || ""}
                            onValueChange={(v) => setForm({ ...form, answers: { ...form.answers, [q.key]: v } })}>
                      <SelectTrigger data-testid={`question-${q.key}`}><SelectValue placeholder="Choose" /></SelectTrigger>
                      <SelectContent>
                        {(q.type === "yes_no" ? ["Yes", "No"] : q.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input type={q.type === "date" ? "date" : q.type === "number" ? "number" : "text"}
                           value={form.answers[q.key] || ""} data-testid={`question-${q.key}`}
                           onChange={(e) => setForm({ ...form, answers: { ...form.answers, [q.key]: e.target.value } })} />
                  )}
                </Field>
              ))}
            </Section>
          )}

          <Section title="Anything else" testid="extras-section">
            <Field label="Why you would like to work with us (optional)">
              <Textarea className="min-h-28" value={form.cover_note} data-testid="cover-note" onChange={set("cover_note")} />
            </Field>
            <Field label="Need help applying, or a reasonable accommodation? (private)">
              <Textarea className="min-h-20" value={form.accommodation_request} data-testid="accommodation-request"
                        placeholder="This goes to an administrator privately and is not shown to the people scoring applications."
                        onChange={set("accommodation_request")} />
            </Field>
          </Section>

          <Section title="Voluntary equal-opportunity questions" testid="demographics-section"
                   hint="Every question is optional. Your answers are stored separately from your application, are never shown to the people reviewing you, and play no part in hiring decisions.">
            {DEMOGRAPHIC_QUESTIONS.map(([key, label, options]) => (
              <Field key={key} label={label}>
                <Select value={ack.demographics[key] || ""}
                        onValueChange={(v) => setAck({ ...ack, demographics: { ...ack.demographics, [key]: v } })}>
                  <SelectTrigger data-testid={`demographic-${key}`}><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            ))}
          </Section>

          <Section title="Review and submit" testid="submit-section">
            <label className="flex gap-3 items-start text-sm font-semibold">
              <Checkbox checked={ack.acknowledged} data-testid="ack-checkbox"
                        onCheckedChange={(v) => setAck({ ...ack, acknowledged: Boolean(v) })} />
              <span>{record.acknowledgment_text}</span>
            </label>
            <Field label="Type your full legal name">
              <Input value={ack.typed_name} data-testid="ack-name" onChange={(e) => setAck({ ...ack, typed_name: e.target.value })} />
            </Field>
            <label className="flex gap-3 items-start text-sm font-semibold">
              <Checkbox checked={ack.pool_opt_in} data-testid="pool-opt-in"
                        onCheckedChange={(v) => setAck({ ...ack, pool_opt_in: Boolean(v) })} />
              <span>{record.pool_text}</span>
            </label>
            {savedAt && <p className="text-xs text-muted-foreground">Draft saved {new Date(savedAt).toLocaleTimeString()}</p>}
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" onClick={() => save(false)} disabled={busy} className="rounded-full font-bold"
                      data-testid="save-draft-btn"><FloppyDisk className="mr-2" /> Save draft</Button>
              <Button onClick={submit} disabled={busy || !ack.acknowledged} className="rounded-full font-bold bg-[#3B82F6] hover:bg-[#2563EB] px-7"
                      data-testid="submit-application-btn">{busy ? "Submitting…" : "Submit application"}</Button>
            </div>
          </Section>
        </div>
      )}
    </PublicShell>
  );
}
